/**
 * Session and workspace-availability text.
 *
 * The words live in the message catalogs (lib/i18n/catalogs, keys under
 * `session.*`); screens translate them with useTranslation(). This file keeps
 * the keys, plus SESSION_MESSAGES: the ENGLISH text, for code that stores a
 * message rather than showing one (the API client leaves `sessionEnded` in
 * the auth store as the sign-in notice). A stored notice is translated when
 * shown, by translateNotice().
 *
 * The workspace copy follows the error catalog (docs/help/errors.md,
 * TENANT_SUSPENDED and TENANT_NOT_ACTIVE), which also holds the Arabic.
 */
import { createTranslator } from "@/lib/i18n/core";
import { en } from "@/lib/i18n/catalogs/en";
import type { MessageKey, TFunction } from "@/lib/i18n/types";
import type { WorkspaceBlockKind } from "./workspace-block";

export const WORKSPACE_BLOCK_KEYS: Record<
  WorkspaceBlockKind,
  { title: MessageKey; body: MessageKey }
> = {
  suspended: {
    title: "session.workspaceBlock.suspended.title",
    body: "session.workspaceBlock.suspended.body",
  },
  provisioning: {
    title: "session.workspaceBlock.provisioning.title",
    body: "session.workspaceBlock.provisioning.body",
  },
  legalHold: {
    title: "session.workspaceBlock.legalHold.title",
    body: "session.workspaceBlock.legalHold.body",
  },
  closed: {
    title: "session.workspaceBlock.closed.title",
    body: "session.workspaceBlock.closed.body",
  },
  notFound: {
    title: "session.workspaceBlock.notFound.title",
    body: "session.workspaceBlock.notFound.body",
  },
  notActive: {
    title: "session.workspaceBlock.notActive.title",
    body: "session.workspaceBlock.notActive.body",
  },
};

// English only, whatever the active language: these strings are stored, not
// shown (see above).
const english = createTranslator({ catalogs: { en, ar: {} } });
const enT = ((key: string, params?: Record<string, string | number>) =>
  english.translate(
    { language: "en", formatLocale: "en-US" },
    key,
    params,
  )) as TFunction;

function blockText(kind: WorkspaceBlockKind) {
  const keys = WORKSPACE_BLOCK_KEYS[kind];
  return { title: enT(keys.title), body: enT(keys.body) };
}

export const SESSION_MESSAGES = {
  sessionEnded: enT("session.ended"),
  /**
   * Left by WorkspaceCreationPage: the session that created a workspace
   * cannot open it, so the person signs in again (see that page).
   */
  workspaceCreated: enT("session.workspaceCreated"),
  /**
   * The same, for someone who already had a workspace: sign-in opens the
   * first of theirs by name and offers no choice yet (D50), so "sign in again
   * to open it" would not be true for them.
   */
  workspaceCreatedMultiple: enT("session.workspaceCreatedMultiple"),
  /**
   * Left by refresh.ts when the server paused the refresh for want of a
   * second factor (W2.8b, D1002): signing in again routes to enrolment.
   */
  mfaRequired: enT("session.mfaRequired"),
  /** Chosen by workspaceBlockKind() (lib/auth/workspace-block.ts). */
  workspaceBlock: {
    suspended: blockText("suspended"),
    provisioning: blockText("provisioning"),
    legalHold: blockText("legalHold"),
    closed: blockText("closed"),
    notFound: blockText("notFound"),
    notActive: blockText("notActive"),
  },
  checkAgain: enT("session.checkAgain"),
  checking: enT("session.checking"),
  stillUnavailable: enT("session.stillUnavailable"),
  details: enT("common.details"),
  signOut: enT("session.signOut"),
  signingOut: enT("session.signingOut"),
} as const;

/** A notice left in the auth store, in the active language. */
export function translateNotice(notice: string, t: TFunction): string {
  if (notice === SESSION_MESSAGES.sessionEnded) return t("session.ended");
  if (notice === SESSION_MESSAGES.workspaceCreated)
    return t("session.workspaceCreated");
  if (notice === SESSION_MESSAGES.workspaceCreatedMultiple)
    return t("session.workspaceCreatedMultiple");
  if (notice === SESSION_MESSAGES.mfaRequired) return t("session.mfaRequired");
  return notice;
}
